import { useCallback } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ActivityAction, ActivityItem } from 'app/data/types/activity';
import type { CheckInPhase } from 'app/shared/hooks/use-check-in-feedback';
import { useActivityCardCopy } from './hooks/use-activity-card-copy';
import { CardShell } from './parts/card-shell';
import { CardHeader } from './parts/card-header';
import { CardStatusFooter } from './parts/card-status-footer';
import { CardRatingFooter } from './parts/card-rating-footer';
import { CardCancelFooter } from './parts/card-cancel-footer';
import { CardEventActionsFooter } from './parts/card-event-actions-footer';

interface ActivityCardProps {
  item: ActivityItem;
  /** Whether the primary action can be routed — a non-tappable card renders without tap affordance. */
  tappable: boolean;
  /** Whether the item's `book_again` action (if any) can be routed. Gates the "Book again" button. */
  canBookAgain: boolean;
  /** Whether the item is a cancellable upcoming visit — gates the inline "Cancel visit" footer. */
  canCancel: boolean;
  /** Whether self check-in is open for this booking — gates the inline "Check in" action. */
  canCheckIn: boolean;
  /** Check-in request in flight for THIS card. */
  onCheckIn: (item: ActivityItem) => void;
  /** Whether self-cancellation is still open — gates the inline "Cancel" action. */
  canCancelEvent: boolean;
  onCancelEvent: (item: ActivityItem) => void;
  /** Resource bookings cancel through their transaction, so they carry their own flag and handler. */
  canCancelBooking: boolean;
  onCancelBooking: (item: ActivityItem) => void;
  /** Likewise check-in: a booking's window comes off its own check_in row, not an event's session. */
  canCheckInBooking: boolean;
  onCheckInBooking: (item: ActivityItem) => void;
  /**
   * This item's confirm beat after a successful check-in — ONE state for both flows, since one card
   * only ever offers one check-in. `done` retires the CTA locally, without waiting for the projection.
   */
  checkInPhase?: CheckInPhase;
  onOpen: (item: ActivityItem, action?: ActivityAction) => void;
  onCancel: (item: ActivityItem) => void;
  rating: number;
  /** Whether this item has feedback to give or already given — gates the stars. */
  showRating?: boolean;
  /** The building's icon for this vertical; the card does not resolve it itself. */
  Icon: LucideIcon;
  /** Feedback already given — the stars show it but can't change it. */
  ratingReadOnly?: boolean;
  onRate: (id: string, rating: number) => void;
  /** Whether this card is the record currently open beside the list. */
  isSelected?: boolean;
}

/**
 * One feed card: type icon, when/title/where, status pill. Open requests pin the title and grow a
 * status footer; a completed item grows a footer when it has feedback to give or a repeat action. Tapping the card — or an action
 * button — deeplinks OUT to the source module via `onOpen`.
 */
export const ActivityCard = ({
  item,
  tappable,
  canBookAgain,
  canCancel,
  canCheckIn,
  canCancelEvent,
  canCancelBooking,
  canCheckInBooking,
  checkInPhase = 'idle',
  onOpen,
  onCancel,
  onCheckIn,
  onCancelEvent,
  onCancelBooking,
  onCheckInBooking,
  rating,
  showRating = false,
  Icon,
  ratingReadOnly = false,
  onRate,
  isSelected = false,
}: ActivityCardProps): JSX.Element => {
  const copy = useActivityCardCopy(item);
  const { isOpen, actionNeeded, whenText, where, updatedMeta, primaryAction, bookAgainAction, actionLabel } = copy;

  const handleOpen = useCallback(() => onOpen(item), [onOpen, item]);
  const handleAction = useCallback(() => onOpen(item, primaryAction), [onOpen, item, primaryAction]);
  const handleBookAgain = useCallback(() => onOpen(item, bookAgainAction), [onOpen, item, bookAgainAction]);
  const handleCancel = useCallback(() => onCancel(item), [onCancel, item]);
  const handleCheckIn = useCallback(() => onCheckIn(item), [onCheckIn, item]);
  const handleCancelEvent = useCallback(() => onCancelEvent(item), [onCancelEvent, item]);
  const handleCancelBooking = useCallback(() => onCancelBooking(item), [onCancelBooking, item]);
  const handleCheckInBooking = useCallback(() => onCheckInBooking(item), [onCheckInBooking, item]);
  // The confirm beat owns the CTA's visibility in both directions. Success settles the feed while the
  // beat runs, so a projection that answers quickly withdraws `canCheckIn` mid-hold and the button
  // would vanish before it had confirmed anything — the failure this whole beat exists to prevent.
  // And once the beat is `done` the CTA is gone for good here, whether or not the projection agrees yet.
  const midConfirmBeat = checkInPhase === 'confirmed' || checkInPhase === 'leaving';
  const showCheckIn = (canCheckIn || canCheckInBooking || midConfirmBeat) && checkInPhase !== 'done';
  // One row serves check-in and cancel across events and bookings; it renders if any of them applies.
  const showActionsFooter = showCheckIn || canCancelEvent || canCancelBooking;
  const handleRate = useCallback((n: number) => onRate(item.id, n), [onRate, item.id]);

  return (
    <CardShell
      tappable={tappable}
      onActivate={handleOpen}
      testId={`activity-card-${item.id}`}
      type={item.type}
      isSelected={isSelected}
    >
      <CardHeader item={item} isOpen={isOpen} whenText={whenText} where={where} Icon={Icon} />
      {/* Not alongside the rating footer: a resolved request would carry two footers, and the rating is
          the affordance that matters once the work is done. */}
      {isOpen && !showRating && (
        <CardStatusFooter
          updatedMeta={updatedMeta}
          actionNeeded={actionNeeded}
          tappable={tappable}
          action={primaryAction}
          actionLabel={primaryAction ? actionLabel(primaryAction) : ''}
          onAction={handleAction}
        />
      )}
      {(showRating || canBookAgain) && (
        <CardRatingFooter
          rating={rating}
          showRating={showRating}
          ratingReadOnly={ratingReadOnly}
          onRate={handleRate}
          canBookAgain={canBookAgain}
          onBookAgain={handleBookAgain}
        />
      )}
      {showActionsFooter && (
        <CardEventActionsFooter
          canCheckIn={showCheckIn}
          onCheckIn={canCheckInBooking ? handleCheckInBooking : handleCheckIn}
          checkInPhase={checkInPhase}
          canCancel={canCancelEvent || canCancelBooking}
          onCancel={canCancelBooking ? handleCancelBooking : handleCancelEvent}
        />
      )}
      {canCancel && <CardCancelFooter onCancel={handleCancel} />}
    </CardShell>
  );
};
